import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

function safeNextFromHash(raw) {
  const value = (raw || '').trim();
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('://')) {
    return '/dashboard';
  }
  return value;
}

/**
 * Completes first-party Google OAuth after the backend redirects here with
 * `#access_token=...&next=/dashboard`. Stores the JWT like email/password login.
 *
 * Legacy Emergent `#session_id=` is no longer used (backend returns 410 unless
 * ALLOW_EMERGENT_GOOGLE_AUTH is explicitly enabled).
 */
const AuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = (window.location.hash || '').replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const nextPath = safeNextFromHash(params.get('next'));

    // Clear the hash immediately so the JWT is not left in the address bar.
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

    if (!accessToken) {
      toast.error('Google sign-in failed. Please try again.');
      navigate('/auth', { replace: true });
      return;
    }

    (async () => {
      try {
        localStorage.setItem('token', accessToken);
        const meRes = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        });
        if (meRes.ok) {
          const user = await meRes.json();
          if (setUser) setUser(user);
          toast.success('Signed in with Google!');
          navigate(nextPath, { replace: true });
        } else {
          localStorage.removeItem('token');
          toast.error('Google sign-in failed. Please try again.');
          navigate('/auth', { replace: true });
        }
      } catch (e) {
        localStorage.removeItem('token');
        toast.error('Google sign-in failed. Please try again.');
        navigate('/auth', { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-zinc-300" data-testid="google-auth-callback">
      <Sparkles className="w-8 h-8 text-violet-400 animate-pulse" />
      <p className="text-sm">Signing you in…</p>
    </div>
  );
};

export default AuthCallback;
